import axios from 'axios';
import { InventoryItem } from '../utils/types';
import { getStoreSettings } from './storeSettingsService';

const FB_GRAPH_URL = 'https://graph.facebook.com/v19.0';

/**
 * الحصول على معلومات الصفحات المرتبطة بالـ Access Token
 * هذه الدالة تكشف الـ Page ID تلقائياً من الـ Token
 */
export const getPageInfoFromToken = async (accessToken: string): Promise<{
  success: boolean;
  pages: Array<{ id: string; name: string; accessToken: string }>;
  message: string;
}> => {
  try {
    // الحصول على الصفحات المرتبطة بالحساب فقط (وليس الحساب الشخصي)
    const response = await axios.get(`${FB_GRAPH_URL}/me/accounts`, {
      params: {
        access_token: accessToken,
        fields: 'id,name,access_token,category'
      }
    });

    if (response.data && response.data.data && response.data.data.length > 0) {
      const pages = response.data.data.map((page: any) => ({
        id: page.id,
        name: page.name,
        accessToken: page.access_token
      }));
      return {
        success: true,
        pages,
        message: `✅ تم العثور على ${pages.length} صفحة. اختر الصفحة المطلوبة.`
      };
    }

    // لم يتم العثور على صفحات - الـ Token ربما يكون User Token بدون صلاحيات الصفحات
    return {
      success: false,
      pages: [],
      message: `⚠️ لم يتم العثور على صفحات مرتبطة بهذا المفتاح.\n\nالحل:\n1. اذهب إلى Graph API Explorer\n2. اختر التطبيق\n3. من قائمة "User or Page" اختر اسم صفحتك (وليس User Token)\n4. أضف صلاحية pages_manage_posts\n5. اضغط Generate Access Token\n6. انسخ الـ Token الجديد وجربه هنا`
    };
  } catch (error: any) {
    console.error('Error getting page info:', error);
    const errorMessage = error.response?.data?.error?.message || error.message || 'خطأ غير معروف';
    return {
      success: false,
      pages: [],
      message: `❌ خطأ في الاتصال: ${errorMessage}`
    };
  }
};

/**
 * اختبار اتصال فيسبوك والتحقق من صحة الإعدادات
 */
export const testFacebookConnection = async (pageId: string, accessToken: string): Promise<{
  success: boolean;
  message: string;
  pageName?: string;
}> => {
  try {
    const response = await axios.get(`${FB_GRAPH_URL}/${pageId}`, {
      params: {
        access_token: accessToken,
        fields: 'id,name,fan_count'
      }
    });

    if (response.data && response.data.id) {
      return {
        success: true,
        message: `✅ الاتصال ناجح! الصفحة: ${response.data.name}`,
        pageName: response.data.name
      };
    }

    return { success: false, message: 'فشل التحقق من الصفحة' };
  } catch (error: any) {
    console.error('Error testing Facebook connection:', error);
    const errorMessage = error.response?.data?.error?.message || error.message || 'خطأ غير معروف';
    
    if (errorMessage.includes('not allowed')) {
      return { success: false, message: '❌ المعرف المستخدم هو حساب شخصي وليس صفحة. استخدم زر "كشف تلقائي" للحصول على معرف الصفحة الصحيح.' };
    }
    if (errorMessage.includes('Invalid OAuth')) {
      return { success: false, message: '❌ مفتاح الوصول غير صالح أو منتهي الصلاحية.' };
    }

    return { success: false, message: `❌ خطأ: ${errorMessage}` };
  }
};

/**
 * خدمة النشر على صفحة فيسبوك
 */
export const postProductToFacebook = async (product: InventoryItem): Promise<{ success: boolean; message: string; postId?: string }> => {
  try {
    const settings = await getStoreSettings();
    const fbSettings = settings.general.facebookIntegration;

    if (!fbSettings || !fbSettings.isEnabled || !fbSettings.accessToken || !fbSettings.pageId) {
      return { success: false, message: 'إعدادات فيسبوك غير مفعلة أو غير مكتملة. اذهب إلى إعدادات المتجر -> فيسبوك.' };
    }

    console.log(`📘 Posting to Facebook Page ID: ${fbSettings.pageId}`);

    const productUrl = `${window.location.origin}/product/${product.id}`;
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.');

    const defaultTemplate = `🌟 منتج جديد متوفر الآن!
📦 {name}
💰 السعر: {price} ج.م
📝 {description}

🛒 اشتري الآن من خلال الرابط:
{link}`;

    const template = fbSettings.postTemplate || defaultTemplate;
    
    // استبدال المتغيرات بقيم المنتج الحقيقية
    const message = template
      .replace(/{name}/g, product.name || '')
      .replace(/{price}/g, (product.sellingPrice || 0).toString())
      .replace(/{description}/g, product.description || '')
      .replace(/{link}/g, productUrl)
      .trim();

    const hasValidImage = product.imageUrl && 
                          product.imageUrl.startsWith('http') && 
                          !product.imageUrl.includes('localhost') && 
                          !product.imageUrl.includes('127.0.0.1') && 
                          !product.imageUrl.includes('192.168.');

    let endpoint = `${FB_GRAPH_URL}/${fbSettings.pageId}/feed`;
    const postData: any = {
      message: message,
      access_token: fbSettings.accessToken
    };

    if (hasValidImage) {
      // نشر صورة مع النص
      endpoint = `${FB_GRAPH_URL}/${fbSettings.pageId}/photos`;
      postData.url = product.imageUrl;
    } else if (!isLocalhost) {
      // نشر نص مع رابط فقط (إذا لم توجد صورة)
      postData.link = productUrl;
    }

    // طلب النشر لفيسبوك
    const response = await axios.post(endpoint, postData);

    if (response.data && response.data.id) {
      return { success: true, message: 'تم النشر بنجاح على فيسبوك! 🎉', postId: response.data.id };
    }

    return { success: false, message: 'فشل النشر على فيسبوك.' };
  } catch (error: any) {
    console.error('Error posting to Facebook:', error);
    const errorMessage = error.response?.data?.error?.message || error.message || 'خطأ غير معروف';
    
    if (errorMessage.includes('not allowed')) {
      return { success: false, message: 'فشل النشر: المعرف المستخدم غير صحيح. اذهب لإعدادات فيسبوك واستخدم "كشف تلقائي" للحصول على المعرف الصحيح.' };
    }
    
    return { success: false, message: `خطأ أثناء النشر: ${errorMessage}` };
  }
};
